package com.example.hukakmp.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.example.hukakmp.data.TournamentRepository
import com.example.hukakmp.model.Tournament
import com.example.hukakmp.model.TournamentScoreType
import com.example.hukakmp.model.TournamentSummary
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class TournamentViewModel(
    private val repository: TournamentRepository,
) : ViewModel() {

    data class UiState(
        val tournaments: List<TournamentSummary> = emptyList(),
        val isLoading: Boolean = false,
        val error: String? = null,
        val createdCode: String? = null,
        val foundTournament: Tournament? = null,
        val requestSent: Boolean = false,
    )

    private val _uiState = MutableStateFlow(UiState())
    val uiState: StateFlow<UiState> = _uiState.asStateFlow()

    init {
        refresh()
    }

    fun refresh() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.getMyTournaments()
                .onSuccess { tournaments ->
                    _uiState.update {
                        it.copy(
                            tournaments = tournaments,
                            isLoading = false,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message ?: "No se pudieron cargar los torneos.",
                        )
                    }
                }
        }
    }

    fun create(
        name: String,
        description: String,
        startDate: String,
        endDate: String,
        scoreType: TournamentScoreType,
    ) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null, createdCode = null) }
            repository.createTournament(name, description, startDate, endDate, scoreType)
                .onSuccess { tournament ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            createdCode = tournament.codigoInvitacion,
                        )
                    }
                    refresh()
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message ?: "No se pudo crear el torneo.",
                        )
                    }
                }
        }
    }

    fun search(code: String) {
        viewModelScope.launch {
            _uiState.update {
                it.copy(
                    isLoading = true,
                    error = null,
                    foundTournament = null,
                    requestSent = false,
                )
            }
            repository.searchByCode(code)
                .onSuccess { tournament ->
                    _uiState.update {
                        if (tournament == null) {
                            it.copy(
                                isLoading = false,
                                error = "Código no encontrado. Revisá que esté bien escrito.",
                            )
                        } else {
                            it.copy(
                                isLoading = false,
                                foundTournament = tournament,
                            )
                        }
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message ?: "No se pudo buscar el torneo.",
                        )
                    }
                }
        }
    }

    fun requestJoin(tournamentId: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.requestJoin(tournamentId)
                .onSuccess {
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            requestSent = true,
                            foundTournament = null,
                        )
                    }
                    refresh()
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message ?: "No se pudo enviar la solicitud.",
                        )
                    }
                }
        }
    }

    fun respond(tournamentId: String, participantId: String, accept: Boolean) {
        viewModelScope.launch {
            repository.respondRequest(tournamentId, participantId, accept)
                .onSuccess { refresh() }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(error = error.message ?: "No se pudo responder la solicitud.")
                    }
                }
        }
    }

    fun rejectReport(
        tournamentId: String,
        reportId: String,
        reason: String,
    ) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            repository.rejectReport(tournamentId, reportId, reason)
                .onSuccess { refresh() }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = error.message ?: "No se pudo rechazar el parte.",
                        )
                    }
                }
        }
    }

    fun dismissCreatedCode() = _uiState.update { it.copy(createdCode = null) }
    fun dismissFoundTournament() = _uiState.update { it.copy(foundTournament = null) }
    fun clearRequestSent() = _uiState.update { it.copy(requestSent = false) }
    fun clearError() = _uiState.update { it.copy(error = null) }
}
